import { Box, Checkbox, CheckboxProps } from "@mui/material";
import { useRecoilState } from "recoil";
import { themePalette } from "../../theme";
import {
  FormControlLabelWrapper,
  StyledFormPrimaryLabelClinical,
  StyledFormSecondaryLabelClinical,
  StyledFormCodeContentClinical,
  IndicatorFieldGrid,
  StyledIndicatorSectionDiv,
  NewRequisitionTextField,
} from "./NewRequisition.styles";
import { currentNewRequisitionEdit } from "../../recoil/atoms";
import {
  checkAneuploidyICDCodes,
  checkCarrierICDCodes,
  checkFetalAntigenICDCodes,
  checkRhdICDCodes,
} from "./helpers";
import { getTrimester, parseYmdAtNoonUtc } from "../../utils/helpers";
import {
  onChangeAneuploidyScreenO0951,
  onChangeRhDScreenZ31,
  onChangeRhDScreenO036,
  onChangeAneuploidyScreenO0952,
  onChangeAneuploidyScreenO098,
  onChangeCarrierScreenZ31,
  onChangeCarrierScreenZ810,
  onChangeCarrierScreenZ84,
  onChangeCarrierScreenZ348,
  onChangeAneuploidyScreenZ348,
  onChangeFetalAntigenO36,
  onChangeRhDScreenO283,
  onAneuploidyScreenO285,
  onChangeAneuploidyScreenZ340,
} from "./ICD10CodeLogic";

export interface ClinicalIndicationOptionProps extends CheckboxProps {
  labelContent: string;
  codes: string[];
}
export interface ClinicalIndicationOptionPropsIsFemale extends ClinicalIndicationOptionProps {
  patientSex: string;
}
export interface ClinicalIndicationOptionPropsTrimester extends ClinicalIndicationOptionProps {
  trimester: number;
}
export interface ErrorProps {
  isError: boolean;
}

const ClinicalIndicationOptionPatient: React.FC<
  ClinicalIndicationOptionProps
> = ({ checked, labelContent, codes, onChange }) => {
  const codesHtml: React.ReactElement[] = [];
  codes.forEach((code) => {
    codesHtml.push(
      <StyledFormCodeContentClinical key={code} $shouldBold={checked === true}>
        {code}
      </StyledFormCodeContentClinical>,
    );
  });
  return (
    <IndicatorFieldGrid>
      <FormControlLabelWrapper
        control={<Checkbox onChange={onChange} checked={checked} />}
        label={labelContent}
      />
      {codesHtml}
    </IndicatorFieldGrid>
  );
};

const ClinicalIndicationOptionPatientSex: React.FC<
  ClinicalIndicationOptionPropsIsFemale
> = ({ checked, labelContent, codes, patientSex, onChange }) => {
  const codesHtml: React.ReactElement[] = [];
  codes.forEach((code, index) => {
    // the first icd 10 code is for females, and the second one is for males
    const femaleIndex = 0;
    const maleIndex = 1;
    codesHtml.push(
      <StyledFormCodeContentClinical
        key={code}
        $shouldBold={
          checked === true && // bold/check the code that corresponds to the correct sex
          ((index === femaleIndex && patientSex === "Female") ||
            (index === maleIndex && patientSex === "Male"))
        }
      >
        {code}
      </StyledFormCodeContentClinical>,
    );
  });
  return (
    <IndicatorFieldGrid>
      <FormControlLabelWrapper
        control={<Checkbox onChange={onChange} checked={checked} />}
        label={labelContent}
      />
      {codesHtml}
    </IndicatorFieldGrid>
  );
};

const ClinicalIndicationOptionTrimester: React.FC<
  ClinicalIndicationOptionPropsTrimester
> = ({ checked, labelContent, codes, trimester, onChange }) => {
  const codesHtml: React.ReactElement[] = [];
  codes.forEach((code, index) => {
    codesHtml.push(
      <StyledFormCodeContentClinical
        key={code}
        $shouldBold={checked === true && index + 1 === trimester}
      >
        {code}
      </StyledFormCodeContentClinical>,
    );
  });

  return (
    <IndicatorFieldGrid>
      <FormControlLabelWrapper
        control={<Checkbox onChange={onChange} checked={checked} />}
        label={labelContent}
      />
      {codesHtml}
    </IndicatorFieldGrid>
  );
};

const ClinicalIndicationCodes: React.FC<ErrorProps> = ({ isError }) => {
  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const { requisitionForm } = newRequisition;

  return (
    <>
      {/* Carrier Screen section  */}
      <StyledIndicatorSectionDiv>
        <StyledFormPrimaryLabelClinical
          $dynamicColor={
            requisitionForm.carrierScreen &&
            checkCarrierICDCodes(requisitionForm) &&
            isError
              ? themePalette.redDark
              : themePalette.greyBase
          }
        >
          Unity Carrier Screen with fetal risk assessment
          {requisitionForm.carrierScreen ? "*" : ""}
        </StyledFormPrimaryLabelClinical>
        <br />
        <StyledFormSecondaryLabelClinical $paddingLeft={0}>
          If the patient with singleton pregnancy is found to be a carrier for
          tested disorders, fetal risk assessment will be performed and O28.5
          ICD-10 code will automatically be applied for this test panel. Carrier
          status code determined by patient sex.
        </StyledFormSecondaryLabelClinical>
        <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
          <ClinicalIndicationOptionPatient
            labelContent="Family history of carrier genetic disease"
            codes={["Z84.81"]}
            checked={requisitionForm.Z8481}
            onChange={(e) =>
              onChangeCarrierScreenZ84(e, newRequisition, setNewRequisition)
            }
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
          <ClinicalIndicationOptionPatientSex
            labelContent="Testing for genetic disease carrier status"
            codes={["Z31.430", "Z31.440"]}
            checked={requisitionForm.Z31430 || requisitionForm.Z31440}
            patientSex={requisitionForm.sex}
            onChange={(e) =>
              onChangeCarrierScreenZ31(e, newRequisition, setNewRequisition)
            }
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
          <ClinicalIndicationOptionTrimester
            labelContent="Supervision of other normal pregnancy"
            codes={["Z34.81", "Z34.82", "Z34.83"]}
            checked={
              requisitionForm.Z3481_carrier ||
              requisitionForm.Z3482_carrier ||
              requisitionForm.Z3483_carrier
            }
            trimester={getTrimester(
              parseYmdAtNoonUtc(requisitionForm.estimatedDueDate),
            )}
            onChange={(e) =>
              onChangeCarrierScreenZ348(e, newRequisition, setNewRequisition)
            }
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
          <ClinicalIndicationOptionPatient
            labelContent="Family history of intellectual disabilities"
            codes={["Z81.0"]}
            checked={requisitionForm.Z810}
            onChange={(e) =>
              onChangeCarrierScreenZ810(e, newRequisition, setNewRequisition)
            }
          />
        </Box>

        <NewRequisitionTextField
          style={{ marginTop: "8px" }}
          fullWidth
          placeholder=""
          label="Other Carrier Screen ICD-10 codes"
          variant="standard"
          focused
          value={requisitionForm.otherCarrierScreenICDCodes}
          onChange={(e: { target: { value: string } }) =>
            setNewRequisition({
              ...newRequisition,
              requisitionForm: {
                ...newRequisition.requisitionForm,
                otherCarrierScreenICDCodes: e.target.value,
              },
            })
          }
        />
      </StyledIndicatorSectionDiv>

      {/* Aneuploidy section  */}
      {!requisitionForm.disableAneuploidy && (
        <StyledIndicatorSectionDiv>
          <StyledFormPrimaryLabelClinical
            $dynamicColor={
              requisitionForm.aneuploidyNIPT &&
              checkAneuploidyICDCodes(requisitionForm) &&
              isError
                ? themePalette.redDark
                : themePalette.greyBase
            }
          >
            Unity Aneuploidy NIPT{requisitionForm.aneuploidyNIPT ? "*" : ""}
          </StyledFormPrimaryLabelClinical>
          <StyledFormSecondaryLabelClinical $paddingLeft={8}>
            Code determined by trimester of pregnancy.
          </StyledFormSecondaryLabelClinical>
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <ClinicalIndicationOptionTrimester
              labelContent="Supervision of elderly primigravida"
              codes={["O09.511", "O09.512", "O09.513"]}
              checked={
                requisitionForm.O09511 ||
                requisitionForm.O09512 ||
                requisitionForm.O09513
              }
              trimester={getTrimester(
                parseYmdAtNoonUtc(requisitionForm.estimatedDueDate),
              )}
              onChange={(e) =>
                onChangeAneuploidyScreenO0951(
                  e,
                  newRequisition,
                  setNewRequisition,
                )
              }
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <ClinicalIndicationOptionTrimester
              labelContent="Supervision of elderly multigravida"
              codes={["O09.521", "O09.522", "O09.523"]}
              checked={
                requisitionForm.O09521 ||
                requisitionForm.O09522 ||
                requisitionForm.O09523
              }
              trimester={getTrimester(
                parseYmdAtNoonUtc(requisitionForm.estimatedDueDate),
              )}
              onChange={(e) =>
                onChangeAneuploidyScreenO0952(
                  e,
                  newRequisition,
                  setNewRequisition,
                )
              }
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <ClinicalIndicationOptionTrimester
              labelContent="Supervision of other high risk pregnancies"
              codes={["O09.891", "O09.892", "O09.893"]}
              checked={
                requisitionForm.O09891 ||
                requisitionForm.O09892 ||
                requisitionForm.O09893
              }
              trimester={getTrimester(
                parseYmdAtNoonUtc(requisitionForm.estimatedDueDate),
              )}
              onChange={(e) =>
                onChangeAneuploidyScreenO098(
                  e,
                  newRequisition,
                  setNewRequisition,
                )
              }
            />
          </Box>
          {
            // this is removed on latest versions of the trf
            <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
              <ClinicalIndicationOptionTrimester
                labelContent="Supervision of normal first pregnancy"
                codes={["Z34.01", "Z34.02", "Z34.03"]}
                checked={
                  requisitionForm.Z3401 ||
                  requisitionForm.Z3402 ||
                  requisitionForm.Z3403
                }
                trimester={getTrimester(
                  parseYmdAtNoonUtc(requisitionForm.estimatedDueDate),
                )}
                onChange={(e) =>
                  onChangeAneuploidyScreenZ340(
                    e,
                    newRequisition,
                    setNewRequisition,
                  )
                }
              />
            </Box>
          }
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <ClinicalIndicationOptionTrimester
              labelContent="Supervision of other normal pregnancy"
              codes={["Z34.81", "Z34.82", "Z34.83"]}
              checked={
                requisitionForm.Z3481_aneuploidy ||
                requisitionForm.Z3482_aneuploidy ||
                requisitionForm.Z3483_aneuploidy
              }
              trimester={getTrimester(
                parseYmdAtNoonUtc(requisitionForm.estimatedDueDate),
              )}
              onChange={(e) =>
                onChangeAneuploidyScreenZ348(
                  e,
                  newRequisition,
                  setNewRequisition,
                )
              }
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <ClinicalIndicationOptionPatient
              labelContent="Abnormal ultrasound findings"
              codes={["O28.3"]}
              checked={requisitionForm.O283}
              onChange={(e) =>
                onChangeRhDScreenO283(e, newRequisition, setNewRequisition)
              }
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <ClinicalIndicationOptionPatient
              labelContent="Abnormal chromosomal & genetic findings"
              codes={["O28.5"]}
              checked={requisitionForm.O285_aneuploidy}
              onChange={(e) =>
                onAneuploidyScreenO285(e, newRequisition, setNewRequisition)
              }
            />
          </Box>

          <NewRequisitionTextField
            style={{ marginTop: "8px" }}
            fullWidth
            placeholder=""
            label="Other Aneuploidy NIPT ICD-10 codes"
            variant="standard"
            focused
            value={requisitionForm.otherAneuploidyICDCodes}
            onChange={(e: { target: { value: string } }) =>
              setNewRequisition({
                ...newRequisition,
                requisitionForm: {
                  ...newRequisition.requisitionForm,
                  otherAneuploidyICDCodes: e.target.value,
                },
              })
            }
          />
        </StyledIndicatorSectionDiv>
      )}

      {/* RhD section  */}
      {(requisitionForm.aneuploidyIncludeRhd ||
        requisitionForm.isRhdOnlyOrder) && (
        <StyledIndicatorSectionDiv>
          <StyledFormPrimaryLabelClinical
            $dynamicColor={
              checkRhdICDCodes(requisitionForm) && isError
                ? themePalette.redDark
                : themePalette.greyBase
            }
          >
            Unity RhD NIPT*
          </StyledFormPrimaryLabelClinical>
          <StyledFormSecondaryLabelClinical $paddingLeft={8}>
            Code determined by trimester of pregnancy.
          </StyledFormSecondaryLabelClinical>
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <ClinicalIndicationOptionTrimester
              labelContent="Maternal care for anti-D [Rh] antibodies"
              codes={["O36.0110", "O36.0120", "O36.0130"]}
              checked={
                requisitionForm.O360110 ||
                requisitionForm.O360120 ||
                requisitionForm.O360130
              }
              trimester={getTrimester(
                parseYmdAtNoonUtc(requisitionForm.estimatedDueDate),
              )}
              onChange={(e) =>
                onChangeRhDScreenO036(e, newRequisition, setNewRequisition)
              }
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <ClinicalIndicationOptionPatient
              labelContent="Encounter for Rh incompatibility status"
              codes={["Z31.82"]}
              checked={requisitionForm.Z3182}
              onChange={(e) =>
                onChangeRhDScreenZ31(e, newRequisition, setNewRequisition)
              }
            />
          </Box>
          <NewRequisitionTextField
            style={{ marginTop: "8px" }}
            fullWidth
            placeholder=""
            label="Other RhD ICD-10 codes"
            variant="standard"
            focused
            value={requisitionForm.otherRhdICDCodes}
            onChange={(e: { target: { value: string } }) =>
              setNewRequisition({
                ...newRequisition,
                requisitionForm: {
                  ...newRequisition.requisitionForm,
                  otherRhdICDCodes: e.target.value,
                },
              })
            }
          />
        </StyledIndicatorSectionDiv>
      )}

      {/* Fetal Antigen section  */}
      {requisitionForm.isFetalAntigen && (
        <StyledIndicatorSectionDiv>
          <StyledFormPrimaryLabelClinical
            $dynamicColor={
              checkFetalAntigenICDCodes(requisitionForm) && isError
                ? themePalette.redDark
                : themePalette.greyBase
            }
          >
            Unity fetal antigen NIPT*
          </StyledFormPrimaryLabelClinical>
          <StyledFormSecondaryLabelClinical $paddingLeft={8}>
            Code determined by trimester of pregnancy.
          </StyledFormSecondaryLabelClinical>
          <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <ClinicalIndicationOptionTrimester
              labelContent="Maternal care for other isoimmunization"
              codes={["O36.1910", "O36.1920", "O36.1930"]}
              checked={
                requisitionForm.O361910 ||
                requisitionForm.O361920 ||
                requisitionForm.O361930
              }
              trimester={getTrimester(
                parseYmdAtNoonUtc(requisitionForm.estimatedDueDate),
              )}
              onChange={(e) =>
                onChangeFetalAntigenO36(e, newRequisition, setNewRequisition)
              }
            />
          </Box>
          <NewRequisitionTextField
            style={{ marginTop: "8px" }}
            fullWidth
            placeholder=""
            label="Other Fetal Antigen ICD-10 codes"
            variant="standard"
            focused
            value={requisitionForm.otherFetalAntigenICDCodes}
            onChange={(e: { target: { value: string } }) =>
              setNewRequisition({
                ...newRequisition,
                requisitionForm: {
                  ...newRequisition.requisitionForm,
                  otherFetalAntigenICDCodes: e.target.value,
                },
              })
            }
          />
        </StyledIndicatorSectionDiv>
      )}
    </>
  );
};
export default ClinicalIndicationCodes;
